/**
 * How many sets and which rep target a program entry has, as numbers and as the texts shown for
 * them.
 *
 * Edit Workouts shows these per exercise ("3 Sets | 6/10 reps"), and the Training Overview
 * (#5456) shows the same two facts in its grid ("3", "6/10"). Both read them from here, so the
 * two screens cannot show different targets for the same exercise. Moved out of
 * `EditWorkoutsPage` unchanged; nothing a user sees in Edit Workouts differs.
 *
 * No Angular and no database, so a unit test can drive it (`exercise-plan-texts.test.ts`).
 */

import { subSlotUsesMyoReps } from '@cf-shared/data-model/exercise-myoreps';
import { IntensityLevel, Plan_ExercisePath, Plan_SubSlot, getRepTargetForIntensity, getSlotPathIntensity } from '@cf-shared/data-model/fitness-plan';
import { getEffectivePlanSetCount } from '@cf-shared/data-model/plan-set-count';
import { DeclaredState, FitnessState } from '@cf-shared/data-model/user-state';
import { LL } from 'i18n/i18n.service';
import { Exercise } from '@cf-shared/data-model/exercise';
import { exerciseUsesRMTesting } from '@cf-shared/data-model/exercise-rm-testing';
import type { TranslationFunctions } from '../../gensrc/i18n/i18n-types';

/**
 * The number of sets the program asks for. The workout builder never applies a volume adjustment
 * to a myo-rep exercise, so neither does this count; `subSlotUsesMyoReps` holds that rule.
 */
export function planSetCount(subSlot: Plan_SubSlot, exercise: Exercise, declaredState: DeclaredState, fitnessState: FitnessState): number {
  return getEffectivePlanSetCount(subSlot, subSlotUsesMyoReps(subSlot, exercise, declaredState, fitnessState)) || 1;
}

/** the rep targets of one program entry */
export interface PlanRepTargets {
  /** heaviest day first: 1 number, 2 for an exercise that alternates heavy and light days, 3 with a medium day */
  reps: number[];
  /** true when at least one number is the app's suggestion for a target that was not measured yet (shown with "~") */
  suggested: boolean;
}

/**
 * The rep targets of a program entry, or null when there is nothing to show: an exercise that
 * finds its target by a strength test and was not tested yet, or paths that do not form a known
 * set (one path, heavy and light, or heavy, medium and light).
 */
export function planRepTargets(subSlot: Plan_SubSlot, exercise: Exercise, declaredState: DeclaredState, fitnessState: FitnessState): PlanRepTargets | null {
  const paths = subSlot.exercisePaths;
  if (!paths || paths.length === 0) {
    return null;
  }

  // the levels the paths must hold, heaviest first, for each known number of paths
  let levels: (IntensityLevel | undefined)[];
  switch (paths.length) {
    case 1:
      // single path (Initial/Intensified): whatever level it has
      levels = [undefined];
      break;
    case 2:
      levels = [IntensityLevel.DUPheavy, IntensityLevel.DUPlight];
      break;
    case 3:
      levels = [IntensityLevel.TUPheavy, IntensityLevel.TUPmedium, IntensityLevel.TUPlight];
      break;
    default:
      return null;
  }
  const orderedPaths: Plan_ExercisePath[] = [];
  for (const level of levels) {
    const path = level === undefined ? paths[0] : paths.find(p => p.intensityLevel === level);
    // if a level has no path, the paths do not form a known set
    if (!path) {
      return null;
    }
    orderedPaths.push(path);
  }

  // three-deep fallback during Phase 2A migration window: ExerciseStat preference (Phase 2 model),
  // path.originalTargetReps (legacy), then path.target_reps (system default). Phase 2B drops the
  // middle term together with the deprecated field.
  const exerciseStat = fitnessState.exerciseStats[exercise._id];
  const nominalReps = orderedPaths.map(path =>
    exerciseStat?.preferRepTargetByIntensity?.[path.intensityLevel] ?? path.originalTargetReps ?? path.target_reps);

  // if every target is established, show them as they are
  if (nominalReps.every(reps => reps !== undefined)) {
    return { reps: nominalReps as number[], suggested: false };
  }
  // an exercise that finds its target by a strength test and was not tested yet shows no reps
  // (Edit Workouts shows "RM Testing" instead)
  if (exerciseUsesRMTesting(exercise, declaredState, fitnessState)) {
    return null;
  }
  // not yet rep-tested: fill each missing target with the app's suggestion for that intensity
  return {
    reps: orderedPaths.map((path, i) => nominalReps[i] ?? getRepTargetForIntensity(getSlotPathIntensity(subSlot, path))),
    suggested: true,
  };
}

/** The rep target as Edit Workouts writes it: "8 reps", "6/10 reps", "~6/9/12 reps". Null when there is nothing to show. */
export function planRepTargetText(
  subSlot: Plan_SubSlot,
  exercise: Exercise,
  declaredState: DeclaredState,
  fitnessState: FitnessState,
  ll: TranslationFunctions = LL,
): string | null {
  const targets = planRepTargets(subSlot, exercise, declaredState, fitnessState);
  if (!targets) {
    return null;
  }
  const texts = ll.editWorkouts;
  const [first, second, third] = targets.reps;
  switch (targets.reps.length) {
    case 1:
      return targets.suggested ? texts.suggestedRepsCount({ count: first }) : texts.repsCount({ count: first });
    case 2:
      return targets.suggested
        ? texts.suggestedDupRepsCount({ heavy: first, light: second })
        : texts.dupRepsCount({ heavy: first, light: second });
    default:
      return targets.suggested
        ? texts.suggestedTupRepsCount({ heavy: first, medium: second, light: third })
        : texts.tupRepsCount({ heavy: first, medium: second, light: third });
  }
}

/** The rep target as numbers alone, for a narrow table cell: "8", "6/10", "~6/9/12". Null when there is nothing to show. */
export function planRepTargetShortText(subSlot: Plan_SubSlot, exercise: Exercise, declaredState: DeclaredState, fitnessState: FitnessState): string | null {
  const targets = planRepTargets(subSlot, exercise, declaredState, fitnessState);
  if (!targets) {
    return null;
  }
  return `${targets.suggested ? '~' : ''}${targets.reps.join('/')}`;
}
